import React, { useEffect, useState } from 'react';
import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { useErrorHandler } from 'src/hooks/useErrorHandler';
import { MemberActivity } from './types';
import {
  useGetMemberActivitiesInProgressQuery,
  GetMemberActivitiesInProgressQuery,
  Activity
} from 'src/graphql/generated';

const useFetchAllActivitiesInProgress = () => {
  const handleError = useErrorHandler();
  const { activeMemberId } = React.useContext(AuthContext) as AuthContextType;
  const [allActivities, setAllActivities] = useState<MemberActivity[]>([]);
  const [fetchActivitesError, setFetchActivitesError] = useState<string | undefined>();

  useEffect(() => {
    setAllActivities([]);
  }, [activeMemberId]);

  const { data, loading, error, fetchMore } = useGetMemberActivitiesInProgressQuery({
    variables: { memberId: activeMemberId },
    onError: handleError,
    fetchPolicy: 'cache-and-network'
  });
  useEffect(() => {
    const handleFetchMore = () => {
      const nextToken = data?.getMemberActivitiesInProgress?.nextToken;
      if (nextToken) {
        fetchMore({
          variables: { memberid: activeMemberId, nextToken: nextToken },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            const prevItems = prev.getMemberActivitiesInProgress?.items as Activity[];
            const moreItems = fetchMoreResult.getMemberActivitiesInProgress?.items as Activity[];
            const mergedItems: any = [...prevItems, ...moreItems];

            const mergedResult: GetMemberActivitiesInProgressQuery = {
              getMemberActivitiesInProgress: {
                items: mergedItems,
                nextToken: fetchMoreResult.getMemberActivitiesInProgress?.nextToken
              }
            };

            return mergedResult;
          }
        }).catch((error) => {
          setFetchActivitesError(error);
        });
      }
    };
    handleFetchMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // Trigger useEffect when data changes

  const isLoadingOrLoadingFetchMore = !!(loading || data?.getMemberActivitiesInProgress?.nextToken);
  useEffect(() => {
    if (!isLoadingOrLoadingFetchMore) {
      setAllActivities(data?.getMemberActivitiesInProgress?.items ?? []);
    }
  }, [data, loading, isLoadingOrLoadingFetchMore]);

  return {
    data,
    loading: isLoadingOrLoadingFetchMore,
    error,
    allActivities: allActivities,
    fetchActivitesError
  };
};

export default useFetchAllActivitiesInProgress;
